import { useSelector } from '@xstate/react';
import React, { useEffect, useState } from 'react';

import { SimpleTooltip } from '@eluve/components';
import { Button, Checkbox, Icon, Label, Stack } from '@eluve/components';

import { useSegmentAudioContext } from './SegmentAudioContextStore';

interface SegmentKaraokeLogicProps {
  onGoToLatestEdit?: () => void;
}

const SegmentKaraokeLogic: React.FC<SegmentKaraokeLogicProps> = ({
  onGoToLatestEdit,
}) => {
  const { store, audioPlayerHandle } = useSegmentAudioContext();

  const { activeFragmentId, activeUtteranceId } = useSelector(
    store,
    (state) => ({
      activeFragmentId: state.context.activeFragmentId,
      activeUtteranceId: state.context.activeUtteranceId,
    }),
    (prev, next) =>
      prev.activeFragmentId === next.activeFragmentId &&
      prev.activeUtteranceId === next.activeUtteranceId,
  );

  const [followWord, setFollowWord] = useState(true);
  const [highlightEdits, setHighlightEdits] = useState(false);
  const [repeatUtterance, setRepeatUtterance] = useState(false);

  /**
   * This effect is used to scroll the active word into view
   * dehighlight the previous utterance and word and highlight the current utterance word
   */
  useEffect(() => {
    const previousWord = document.querySelector('[data-karaoke-word-active]');
    const previousUtterance = document.querySelector('[data-karaoke-active]');

    const word = document.querySelector(
      `[data-karaoke-word-id="${activeFragmentId}"]`,
    );
    const utterance = document.querySelector(
      `[data-karaoke-utterance-id="${activeUtteranceId}"]`,
    );

    if (word && followWord) {
      word.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    if (word !== previousWord) {
      if (previousWord) {
        previousWord.removeAttribute('data-karaoke-word-active');
      }
      word?.setAttribute('data-karaoke-word-active', 'true');
    }

    if (utterance !== previousUtterance) {
      if (previousUtterance) {
        previousUtterance.removeAttribute('data-karaoke-active');
      }
      utterance?.setAttribute('data-karaoke-active', 'true');
    }
  }, [activeFragmentId, activeUtteranceId, followWord]);

  /**
   * This effect is used to highlight the edits
   * by adding a class to the edited elements
   */
  useEffect(() => {
    const allEdits = document.querySelectorAll('[data-edited-by]');
    if (allEdits.length === 0) return;
    allEdits.forEach((edit) => {
      if (highlightEdits) {
        edit.classList.add('bg-bg-teal-strong');
      } else {
        edit.classList.remove('bg-bg-teal-strong');
      }
    });
  }, [highlightEdits]);

  /**
   * This update the audio player to start or stop repeating the utterance
   */
  useEffect(() => {
    if (repeatUtterance) {
      audioPlayerHandle.current?.startRepeat();
    } else {
      audioPlayerHandle.current?.clearRepeat();
    }
  }, [audioPlayerHandle, repeatUtterance]);

  return (
    <Stack fullWidth={false} gap={2} direction="horizontal">
      <Button onClick={onGoToLatestEdit}>
        <Icon name="ArrowRightToLine" size="auto" />
        Go to furthest edit
      </Button>
      <Label>
        <Stack direction="horizontal" fullWidth={false} gap={2}>
          <Checkbox
            className="m-0"
            aria-label="Follow Word"
            checked={followWord}
            onCheckedChange={(e) => {
              setFollowWord(Boolean(e));
            }}
          />
          <span>Follow Word</span>
        </Stack>
      </Label>
      <Label>
        <Stack direction="horizontal" fullWidth={false} gap={2}>
          <Checkbox
            className="m-0"
            aria-label="Highlight Edits"
            checked={highlightEdits}
            onCheckedChange={(e) => {
              setHighlightEdits(Boolean(e));
            }}
          />
          <span>Highlight Edits</span>
        </Stack>
      </Label>
      <Label>
        <SimpleTooltip label="This will repeat the utterance when you click on the timestamp.">
          <Stack gap={2} direction="horizontal">
            <Checkbox
              className="m-0"
              aria-label="Repeat Utterance"
              checked={repeatUtterance}
              onCheckedChange={(e) => {
                setRepeatUtterance(Boolean(e));
              }}
            />
            <span>Repeat Utterance</span>
          </Stack>
        </SimpleTooltip>
      </Label>
    </Stack>
  );
};

export default SegmentKaraokeLogic;
